import { LangfuseBrowserClient } from "@langfuse/browser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FEEDBACK_TIMINGS } from "@/constants/feedback-timings";
import { handleAsyncAction } from "@/utils/async-error-handler";
import { env } from "@/env";
import { useWidgetRuntimeConfig } from "@/context/widget-runtime-config";

/** Keep aligned with `POLLEN_API_KEY_HEADER` in `apps/api/src/lib/api-key-utils.ts`. */
const POLLEN_API_KEY_REQUEST_HEADER = "x-pollen-api-key";

type LangfusePublicConfig = {
  baseUrl: string;
  publicKey: string;
  environment?: string;
};

type WidgetConfigResponse = {
  langfuse?: {
    baseUrl?: string;
    publicKey?: string;
    environment?: string;
  } | null;
};

const fallbackLangfuseConfig: LangfusePublicConfig | null
  = env.LANGFUSE_URL && env.LANGFUSE_PUBLIC_KEY
    ? { baseUrl: env.LANGFUSE_URL, publicKey: env.LANGFUSE_PUBLIC_KEY }
    : null;

async function fetchTenantLangfuseConfig(apiUrl: string, apiKey: string): Promise<LangfusePublicConfig | null> {
  try {
    const response = await fetch(`${apiUrl}/v2/widget-config`, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data || typeof data !== "object") {
      return null;
    }

    const responseData = data as WidgetConfigResponse;
    const baseUrl = responseData.langfuse?.baseUrl?.trim();
    const publicKey = responseData.langfuse?.publicKey?.trim();
    const environment = responseData.langfuse?.environment?.trim();

    if (!baseUrl || !publicKey) {
      return null;
    }

    return { baseUrl, publicKey, ...(environment ? { environment } : {}) };
  }
  catch (error) {
    console.warn("Tenant UI Widget: failed to fetch Langfuse config", { apiUrl, error });
    return null;
  }
}

type UseUserFeedbackProps = {
  readonly traceId?: string;
  readonly observationId?: string;
};

export const FEEDBACK_POSITIVE_VALUE = 1;
// `0` is intentional; avoid truthy checks and compare against constants explicitly.
export const FEEDBACK_NEGATIVE_VALUE = 0;

export function useUserFeedback({ traceId, observationId }: UseUserFeedbackProps) {
  const { apiKey, apiUrl, langfuse } = useWidgetRuntimeConfig();
  const [feedback, setFeedback] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [selectedComment, setSelectedComment] = useState<string | null>(null);
  const [showTextArea, setShowTextArea] = useState(false);
  const [freeTextComment, setFreeTextComment] = useState("");
  const commentHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);
  const initialLangfuseConfig
    = langfuse === undefined
      ? fallbackLangfuseConfig
      : langfuse;
  const [langfuseConfig, setLangfuseConfig] = useState<LangfusePublicConfig | null>(
    initialLangfuseConfig,
  );

  useEffect(() => {
    if (langfuse !== undefined) {
      setLangfuseConfig(langfuse);
      return;
    }

    let isDisposed = false;

    void fetchTenantLangfuseConfig(apiUrl, apiKey).then((tenantConfig) => {
      if (isDisposed || !tenantConfig) {
        return;
      }
      setLangfuseConfig(tenantConfig);
    });

    return () => {
      isDisposed = true;
    };
  }, [apiKey, apiUrl, langfuse]);

  const langfuseWeb = useMemo(() => {
    if (!langfuseConfig) {
      return null;
    }

    const usesPollenTelemetryProxy = langfuseConfig.baseUrl.includes("/proxy-telemetry");

    return new LangfuseBrowserClient({
      baseUrl: langfuseConfig.baseUrl,
      publicKey: langfuseConfig.publicKey,
      ...(langfuseConfig.environment ? { environment: langfuseConfig.environment } : {}),
      ...(apiKey && usesPollenTelemetryProxy
        ? {
            additionalHeaders: {
              // Pollen API validates this; Langfuse SDK owns `Authorization` (Bearer public key).
              [POLLEN_API_KEY_REQUEST_HEADER]: apiKey,
            },
          }
        : {}),
    });
  }, [apiKey, langfuseConfig]);

  // Keep trace identifiers captured here so other handlers can depend on this callback.
  const submitToLangfuse = useCallback(async (value: number, comment: string) => {
    if (!langfuseWeb) {
      throw new Error("Langfuse client not available");
    }

    await langfuseWeb.score({
      traceId,
      observationId,
      name: "user-feedback",
      dataType: "NUMERIC",
      value,
      comment,
    });
  }, [langfuseWeb, observationId, traceId]);

  const clearCommentHideTimeout = useCallback(() => {
    if (commentHideTimeoutRef.current !== null) {
      clearTimeout(commentHideTimeoutRef.current);
      commentHideTimeoutRef.current = null;
    }
  }, []);

  const invalidatePendingRequest = useCallback(() => {
    requestIdRef.current += 1;
    clearCommentHideTimeout();
  }, [clearCommentHideTimeout]);

  const isActiveRequest = useCallback((requestId: number) => (
    isMountedRef.current && requestIdRef.current === requestId
  ), []);

  const handleUserFeedback = useCallback((value: number) => {
    if (isSubmitting)
      return;
    if (!langfuseWeb)
      return;

    invalidatePendingRequest();
    setFeedback(value);
    setSelectedComment(null);
    setFreeTextComment("");
    setShowComments(value === FEEDBACK_POSITIVE_VALUE);
    setShowTextArea(value === FEEDBACK_NEGATIVE_VALUE);
    void submitToLangfuse(value, "").catch(() => undefined);
  }, [invalidatePendingRequest, isSubmitting, langfuseWeb, submitToLangfuse]);

  const handleCommentSelection = useCallback(async (comment: string) => {
    if (isSubmitting || feedback === null)
      return;

    invalidatePendingRequest();
    const requestId = requestIdRef.current;

    // If "Something else" is selected, show text area instead
    if (comment === "Something else") {
      setSelectedComment(comment);
      setShowTextArea(true);
      return;
    }

    await handleAsyncAction(
      async () => {
        setSelectedComment(comment);
        await submitToLangfuse(feedback, comment);

        if (!isActiveRequest(requestId))
          return;

        // Hide comments section after brief active state display
        commentHideTimeoutRef.current = setTimeout(() => {
          commentHideTimeoutRef.current = null;
          if (!isActiveRequest(requestId))
            return;
          setShowComments(false);
        }, FEEDBACK_TIMINGS.ACTIVE_STATE_DISPLAY_MS);
      },
      {
        setIsSubmitting: (submitting) => {
          if (isActiveRequest(requestId))
            setIsSubmitting(submitting);
        },
        errorContext: "submit comment feedback",
        onError: () => {
          if (isActiveRequest(requestId))
            setSelectedComment(null);
        },
      },
    );
  }, [feedback, invalidatePendingRequest, isActiveRequest, isSubmitting, submitToLangfuse]);

  const handleFreeTextSubmit = useCallback(async (text: string) => {
    if (isSubmitting || feedback === null)
      return;

    invalidatePendingRequest();
    const requestId = requestIdRef.current;

    await handleAsyncAction(
      async () => {
        await submitToLangfuse(feedback, text);

        // Hide section immediately after successful submission (no timeout for user input)
        if (isActiveRequest(requestId)) {
          setFreeTextComment("");
          setShowComments(false);
          setShowTextArea(false);
        }
      },
      {
        setIsSubmitting: (submitting) => {
          if (isActiveRequest(requestId))
            setIsSubmitting(submitting);
        },
        errorContext: "submit free text feedback",
      },
    );
  }, [feedback, invalidatePendingRequest, isActiveRequest, isSubmitting, submitToLangfuse]);

  const handleBackToComments = useCallback(() => {
    if (isSubmitting)
      return;

    invalidatePendingRequest();
    setShowTextArea(false);
    setSelectedComment(null);
    setFreeTextComment("");
    setShowComments(feedback === FEEDBACK_POSITIVE_VALUE);
  }, [feedback, invalidatePendingRequest, isSubmitting]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
      clearCommentHideTimeout();
    };
  }, [clearCommentHideTimeout]);

  // Computed values
  const shouldShowComments = showComments && !showTextArea;
  // Kept as hook surface: CommentOptions chooses positive vs negative presets from this.
  const isPositive = feedback === FEEDBACK_POSITIVE_VALUE;
  const canSubmitFeedback = !!langfuseWeb;
  // Show controls whenever trace identifiers are present.
  const isVisible = !!traceId && !!observationId;

  return {
    // State
    feedback,
    isSubmitting,
    selectedComment,
    freeTextComment,

    // Computed values
    shouldShowComments,
    shouldShowTextArea: showTextArea,
    isPositive,
    isVisible,
    canSubmitFeedback,

    // Handlers
    handleUserFeedback,
    handleCommentSelection,
    handleFreeTextSubmit,
    setFreeTextComment,
    handleBackToComments,
  };
}
